import styles from "./StudiosAdministrationStudio.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import {
  constructEditStudioHref,
  constructEmployeeHref,
  constructPortalStudioHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import {
  IStudiosAdministrationStudioEmployeesModel,
  IStudiosAdministrationStudioModel,
} from "./models/studiosAdministrationStudioModel";
import { useMemo } from "react";
import { DefaultSection } from "../../../../common/components/common/DefaultSection";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { classNames } from "primereact/utils";
import { SpecificPageNotFound } from "../../../../common/components/errorPages/SpecificPageNotFound";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { copyToClipboard } from "../../../../common/utilities/clipboardUtility";
import {
  getSuccessToast,
  getErrorToast,
} from "../../../../common/infrastructure/utilities/toastUtility";
import { IconButton } from "../../../../common/components/button/iconButton/IconButton";
import { SectionInfoRow } from "../../../../common/components/common/SectionInfoRow";
import { HistoryButton } from "../../../../common/components/history/HistoryButton";
import { STUDIO_HISTORY_CONFIG } from "../../../../common/utilities/studioAuditLogUtility";
import { useAppContext } from "../../../../AppContext";
import { getCurrentUserRole } from "../../../../common/infrastructure/helpers/userHelper";
import { UserRole } from "../../../../common/enums";

export const StudiosAdministrationStudio = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const isGlobalAdmin =
    getCurrentUserRole(currentUser) === UserRole.GlobalAdmin;

  const { data: result, isLoading } =
    useApiQuery<IStudiosAdministrationStudioModel>({
      queryKey: [endpointsMetadata.studio.getStudio, id],
      endpoint: `${endpointsMetadata.studio.getStudio}?studioId=${id}`,
      enabled: id !== undefined,
      retry: false,
      disableErrorToast: false,
      globalIsLoading: false,
    });

  const currentStudio = useMemo(() => result, [result]);

  useAddAdditionalLayoutButton({
    iconType: IconType.EditIcon,
    href: constructEditStudioHref(Number(id)),
    rawPath: routePaths.studioEdit,
  });

  const onStudioLinkGenerationIniitalized = async () => {
    const link = constructPortalStudioHref(result);

    const success = await copyToClipboard(link);

    if (success) {
      getSuccessToast("Линкът е копиран в клипборда!");
    } else {
      getErrorToast("Грешка при копиране на линка");
    }
  };

  const listActionButtons = (
    <div className={styles.actionButtons}>
      {isGlobalAdmin && currentStudio && (
        <HistoryButton
          entityId={currentStudio.id}
          entityCreatedOn={String(currentStudio.createdOn)}
          config={STUDIO_HISTORY_CONFIG}
          label="История на обекта"
        />
      )}
      <IconButton
        iconType={IconType.Copy}
        iconHeight={"30px"}
        iconWidth={"30px"}
        tooltip="Генериране на линк за обекта"
        onClick={onStudioLinkGenerationIniitalized}
      />
    </div>
  );

  const backToStudios = () => {
    navigate(routePaths.studios);
  };

  if (!isLoading && !currentStudio) {
    return (
      <SpecificPageNotFound
        text={"За съжаление няма такът обект."}
        buttonText={"Обратно към списъка със обекти"}
        onButtonClick={backToStudios}
      />
    );
  }

  return (
    <div className={styles.root}>
      <DefaultSection className={styles.root_title}>
        <>
          <div className={styles.root_header_title}>
            <SkeletonItem isLoading={isLoading} value={currentStudio?.name} />
          </div>
          <div className={styles.root_header_email}>
            <SkeletonItem
              isLoading={isLoading}
              value={currentStudio?.email}
              width={200}
            />
          </div>
        </>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <SectionInfoRow
          label={"ID"}
          data={currentStudio?.id}
          isLoading={isLoading}
          skekletonWidth={50}
        />

        <SectionInfoRow
          label={"Телефонен номер"}
          data={currentStudio?.phoneNumber}
          isLoading={isLoading}
          skekletonWidth={150}
        />

        <SectionInfoRow
          label={"Собственик"}
          data={currentStudio?.owner}
          isLoading={isLoading}
          skekletonWidth={160}
        />

        <SectionInfoRow
          label={"Добавено на"}
          data={dateUtility.formatDate(currentStudio?.createdOn)}
          isLoading={isLoading}
          skekletonWidth={130}
        />

        <SectionInfoRow
          label={"Прекратено на"}
          data={dateUtility.formatDate(currentStudio?.cancelledOn)}
          isLoading={isLoading}
          skekletonWidth={120}
        />
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        {/* <SectionInfoRow
          label={"Общ оборот"}
          data={`€ ${currentStudio?.grossRevenueTillNow}`}
          isLoading={isLoading}
          skekletonWidth={100}
        /> */}

        <SectionInfoRow
          label={"Брой уникални клиенти"}
          data={`${currentStudio?.numberUniqueClients} бр.`}
          isLoading={isLoading}
          skekletonWidth={100}
        />
      </DefaultSection>

      <ItemsList
        isLoading={isLoading}
        className={styles.list}
        title="Служители"
        actionButton={listActionButtons}
        noItemsText="Не са открити служители."
        itemTemplate={(item, i, actionButtons, className) => {
          return (
            <StudiosAdministrationStudioEmployee
              className={className}
              isLoading={isLoading}
              item={item}
              key={item?.id ?? i}
              actionButtons={actionButtons}
            />
          );
        }}
        items={currentStudio?.employees ?? []}
        swipeLeftActions={[
          {
            method: "delete",
            enablePrompt: true,
            promptMessage:
              "Сигурни ли сте, че искате да изтриете този служител?",
            endpoint: endpointsMetadata.studio.resignEmployee,
            additionalParams: () => `studioId=${currentStudio?.id}`,
            iconType: IconType.DeleteIcon,
            validator: (item: IStudiosAdministrationStudioEmployeesModel) =>
              !item.isCancelled,
            invalidateKeys: [[endpointsMetadata.studio.getStudio]],
          },
          {
            method: "put",
            endpoint: endpointsMetadata.studio.rehireEmployee,
            enablePrompt: true,
            promptMessage:
              "Сигурни ли сте, че искате да възстановите този служител?",
            additionalParams: () => `studioId=${currentStudio?.id}`,
            iconType: IconType.PutBackIcon,
            validator: (item: IStudiosAdministrationStudioEmployeesModel) =>
              item.isCancelled,
            invalidateKeys: [[endpointsMetadata.studio.getStudio]],
          },
        ]}
        swipeRightActions={[
          {
            type: "edit",
            iconType: IconType.EditIcon,
            endpoint: routePaths.employeeEdit,
          },
        ]}
        enableSearch
        fullHeight
      />
    </div>
  );
};

interface IStudiosAdministrationStudioEmployeeProps {
  className?: string;
  item: IStudiosAdministrationStudioEmployeesModel | undefined;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
}

export const StudiosAdministrationStudioEmployee = ({
  className,
  item,
  isLoading,
  actionButtons,
}: IStudiosAdministrationStudioEmployeeProps) => {
  const navigate = useNavigate();

  const onEmployeeClick = () => {
    if (item) {
      const href = constructEmployeeHref(item?.id);
      navigate(href);
    }
  };
  return (
    <div
      className={classNames(className, styles.item, {
        [styles.item_cancelled]: item?.isCancelled,
      })}
      onClick={onEmployeeClick}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          value={
            <div className={styles.item_left_name}>
              {item?.name}
              <span className={styles.item_left_name_id}>(ID: {item?.id})</span>
            </div>
          }
          width={200}
        />
      </div>

      {actionButtons}
      <Icon type={IconType.RightChevronIcon} />
    </div>
  );
};
